import React, { useEffect, useRef } from "react";
import {
  Box, Button, Dialog, DialogContent, DialogTitle,
  TextField, Typography, MenuItem, Select, FormControl,
  InputLabel, IconButton, Tooltip, Chip, Divider,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { useCreateSmsTemplateMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ISmsTemplate } from "../../../Api/Interface/api.interface";

// ── Variables ─────────────────────────────────────────────────────────────────
const VARIABLES = [
  { label: "Customer Name",   tag: "{customer_name}",   desc: "Full name of the customer" },
  { label: "Trip Date",       tag: "{trip_date}",        desc: "Scheduled trip date" },
  { label: "Pickup Time",     tag: "{pickup_time}",      desc: "Pickup time" },
  { label: "Drop-off Time",   tag: "{dropoff_time}",     desc: "Drop-off time" },
  { label: "Pickup Address",  tag: "{pickup_address}",   desc: "Pickup location" },
  { label: "Drop-off Address",tag: "{dropoff_address}",  desc: "Drop-off location" },
  { label: "Driver Name",     tag: "{driver_name}",      desc: "Assigned driver name" },
  { label: "Driver Phone",    tag: "{driver_phone}",     desc: "Assigned driver phone" },
  { label: "Bus Type",        tag: "{bus_type}",         desc: "Type/category of bus" },
  { label: "Passengers",      tag: "{passengers}",       desc: "Number of passengers" },
  { label: "Company Name",    tag: "{company_name}",     desc: "Your company name" },
  { label: "Quote Number",    tag: "{quote_number}",     desc: "Quote / work-order number" },
  { label: "Invoice Number",  tag: "{invoice_number}",   desc: "Invoice reference" },
  { label: "Amount Due",      tag: "{amount_due}",       desc: "Outstanding balance" },
  { label: "Payment Link",    tag: "{payment_link}",     desc: "Online payment URL" },
];

const smsInfo = (len: number) => {
  if (len === 0) return { parts: 0, limit: 160, color: "#64748b" };
  if (len <= 160) return { parts: 1, limit: 160, color: len > 140 ? "#f59e0b" : "#16a34a" };
  const parts = Math.ceil(len / 153);
  return { parts, limit: parts * 153, color: len > parts * 153 - 10 ? "#f59e0b" : "#ef4444" };
};

interface Props {
  open: boolean;
  onClose: () => void;
  data: ISmsTemplate;
}

const SmsEditDialog = ({ open, onClose, data }: Props) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [save, { isLoading }] = useCreateSmsTemplateMutation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formik = useFormik({
    initialValues: {
      name:      data.name,
      category:  data.category,
      message:   data.message,
      is_active: data.is_active,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name.trim()) errors.name = "Template name is required";
      if (!values.message.trim()) errors.message = "Message body is required";
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const res = await save({
          id:        data.id,
          name:      values.name,
          category:  values.category,
          message:   values.message,
          is_active: values.is_active,
        }).unwrap();
        if (!res.status) { showErrorMessage(res.message); return; }
        showMessage(res.message || "Template saved");
        onClose();
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  const insertVariable = (tag: string) => {
    const el = textareaRef.current;
    if (el) {
      const start = el.selectionStart;
      const end   = el.selectionEnd;
      const current = formik.values.message;
      const next = current.slice(0, start) + tag + current.slice(end);
      formik.setFieldValue("message", next);
      // restore cursor after React re-render
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    } else {
      formik.setFieldValue("message", formik.values.message + tag);
    }
  };

  const { parts, limit, color } = smsInfo(formik.values.message.length);
  const isOverLimit = formik.values.message.length > limit;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ background: "linear-gradient(135deg,#0ea5e9,#2f3891)", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SmsOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Edit SMS Template</Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>{data.name}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: "flex", gap: 0 }}>
        {/* ── Left: variable panel ─────────────────────────────────────────── */}
        <Box sx={{ width: 220, flexShrink: 0, borderRight: "1px solid #e2e8f0", bgcolor: "#f8fafc", p: 2, overflow: "auto" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Variables
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {VARIABLES.map((v) => (
              <Tooltip key={v.tag} title={v.desc} placement="right" arrow>
                <Box
                  onClick={() => insertVariable(v.tag)}
                  sx={{
                    px: 1.25, py: 0.75, borderRadius: 1.5,
                    border: "1px solid #e2e8f0", cursor: "pointer",
                    bgcolor: "#fff", transition: "all 0.12s",
                    "&:hover": { bgcolor: "#e0f2fe", borderColor: "#0ea5e9" },
                  }}
                >
                  <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "#1e293b", lineHeight: 1.3 }}>
                    {v.label}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, color: "#0ea5e9", fontFamily: "monospace", lineHeight: 1.3 }}>
                    {v.tag}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {/* ── Right: form ──────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Name */}
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Template Name</Typography>
            <TextField
              fullWidth size="small"
              {...formik.getFieldProps("name")}
              InputProps={{ readOnly: data.is_system, sx: { fontSize: 13, bgcolor: data.is_system ? "#f8fafc" : "#fff" } }}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            {data.is_system && (
              <Typography sx={{ fontSize: 11, color: "#f59e0b", mt: 0.4 }}>
                System template — name cannot be changed
              </Typography>
            )}
          </Box>

          {/* Category */}
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Category</Typography>
            <FormControl size="small" fullWidth disabled={data.is_system}>
              <Select
                value={formik.values.category}
                onChange={(e) => formik.setFieldValue("category", e.target.value)}
                sx={{ borderRadius: 2, fontSize: 13 }}
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Message */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Message Body</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {parts > 1 && (
                  <Chip
                    label={`${parts} SMS parts`}
                    size="small"
                    sx={{ height: 18, fontSize: 9.5, fontWeight: 600, bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" }}
                  />
                )}
                <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>
                  {formik.values.message.length}/{limit}
                </Typography>
              </Box>
            </Box>
            <TextField
              fullWidth multiline rows={9}
              inputRef={textareaRef}
              {...formik.getFieldProps("message")}
              error={(formik.touched.message && Boolean(formik.errors.message)) || isOverLimit}
              helperText={
                (formik.touched.message && formik.errors.message) ||
                (isOverLimit ? `Message exceeds single SMS — will send as ${parts} parts` : undefined)
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2, fontSize: 12.5, fontFamily: "monospace", lineHeight: 1.6, bgcolor: "#f8fafc",
                  "& fieldset": { borderColor: isOverLimit ? "#f59e0b" : "#e2e8f0" },
                  "&:hover fieldset": { borderColor: "#0ea5e9" },
                  "&.Mui-focused fieldset": { borderColor: "#0ea5e9" },
                },
              }}
            />
          </Box>

          {data.is_system && (
            <Button
              size="small" variant="text"
              onClick={() => formik.setFieldValue("message", data.message)}
              sx={{ alignSelf: "flex-start", fontSize: 11, textTransform: "none", color: "#94a3b8", p: 0, "&:hover": { color: "#0ea5e9" } }}
            >
              Reset to default
            </Button>
          )}

          <Divider sx={{ borderColor: "#f1f5f9" }} />

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <Button
              variant="outlined" onClick={onClose}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13, borderColor: "#e2e8f0", color: "#64748b" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isLoading ? undefined : <SaveAltIcon />}
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
              sx={{
                borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13,
                background: "linear-gradient(135deg,#0ea5e9,#2f3891)", px: 2.5,
                boxShadow: "0 2px 8px rgba(14,165,233,0.35)",
                "&:hover": { background: "linear-gradient(135deg,#0284c7,#2f3891)" },
                "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
              }}
            >
              {isLoading ? "Saving…" : "Save Template"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SmsEditDialog;
